import React, { useState, useEffect } from 'react';
import { Bell, Users, Settings, TrendingUp, User, UserCog, LogOut, Edit, Trash2, Key, Ban, Mail, Search, Plus, X, Check, AlertTriangle, Phone, ShieldAlert, Eye, EyeOff, Filter, Download, Upload, UserPlus, Crown, Shield, Globe, Calendar, MapPin, Building, Hash, Camera, RefreshCw, Save, Info, Star, StarOff, ClipboardCopy, Clock, UserCheck, UserX, Send, UserMinus } from 'lucide-react';
import ProfilePicture from './components/ProfilePicture';
import ProfilePictureUpload from './components/ProfilePictureUpload';
import { COUNTRIES, getDisplayName } from './data/userDatabase.ts';
import { User as UserType } from './models/User';
import useEnhancedUserDatabase from './hooks/useEnhancedUserDatabase';
import { sendEmailToUser } from './utils/emailUtils';
import { inviteUserToLogin, sendPasswordResetLink } from './utils/authUtils';

interface UsersAdminProps {
  currentUser: UserType;
  users: UserType[];
  onAddUser: (user: UserType) => Promise<UserType>;
  onUpdateUser: (user: UserType) => Promise<void>;
  onRemoveUser: (userId: string) => Promise<void>;
  onLogout?: () => void;
}

const UsersAdmin: React.FC<UsersAdminProps> = ({ currentUser: user, users, onAddUser, onUpdateUser, onRemoveUser, onLogout = () => {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [currentEditUser, setCurrentEditUser] = useState<UserType | null>(null);
  const [editUserActiveTab, setEditUserActiveTab] = useState<'profile' | 'permissions' | 'security' | 'notes'>('profile');
  const [newUser, setNewUser] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    country: '',
    notes: '',
    isAdmin: false,
    sendEmail: false
  });

  const [showProfilePictureUpload, setShowProfilePictureUpload] = useState(false);
  const [profilePictureUserId, setProfilePictureUserId] = useState<string | null>(null);
  
  // Reset Password modal states
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordStep, setResetPasswordStep] = useState<'options' | 'create-password' | 'success'>('options');
  const [resetPasswordUser, setResetPasswordUser] = useState<UserType | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [emailNewPassword, setEmailNewPassword] = useState(false);
  
  // Enhanced user database hook
  const { profilePictures, isProcessing, error, clearError, utils } = useEnhancedUserDatabase();

  // Add confirmation dialog state
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Add loading states for better UX
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  
  // Add confirmation dialog for Send Invite
  const [showSendInviteConfirmation, setShowSendInviteConfirmation] = useState(false);
  const [userToInvite, setUserToInvite] = useState<UserType | null>(null);
  
  // Add confirmation dialogs for deactivation and deletion
  const [showDeactivateConfirmation, setShowDeactivateConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showDeleteFinalConfirmation, setShowDeleteFinalConfirmation] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<UserType | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserType | null>(null);

  const formatFirestoreTimestamp = (timestamp: any): string => {
    if (!timestamp) return 'Never Logged In';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleNavigation = (page: string) => {
    // Update the URL without reloading the page
    window.history.pushState({}, '', `?page=${page}`);
    window.location.reload();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const [showDeactivatedUsers, setShowDeactivatedUsers] = useState(false);

  const filteredUsers = users.filter((u) => {
    // Regular admins cannot see Super Admins
    if (!user.isSuperAdmin && u.isSuperAdmin) {
      return false;
    }
    
    // Filter out deactivated users unless explicitly shown
    if (u.deactivated && !showDeactivatedUsers) {
      return false;
    }
    
    // Filter by search term
    const searchLower = searchTerm.toLowerCase();
    return (
      u.fullName.toLowerCase().includes(searchLower) ||
      (u.email && u.email.toLowerCase().includes(searchLower))
    );
  });

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const userToAdd: UserType = {
      fullName: newUser.fullName,
      isAdmin: newUser.isAdmin,
      email: newUser.email,
      phoneNumber: newUser.phoneNumber,
      country: newUser.country,
      notes: newUser.notes,
    } as UserType;
    
    try {
      await onAddUser(userToAdd);
      setNewUser({
        fullName: '',
        email: '',
        phoneNumber: '',
        country: '',
        notes: '',
        isAdmin: false,
        sendEmail: false,
      });
      setShowAddUserModal(false);
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Failed to add user in component:', error);
    }
  };

  const handleAddUserAndSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const userToAdd: UserType = {
      fullName: newUser.fullName,
      isAdmin: newUser.isAdmin,
      email: newUser.email,
      phoneNumber: newUser.phoneNumber,
      country: newUser.country,
      notes: newUser.notes,
    } as UserType;
    
    try {
      // First add the user and get the created user data
      const createdUser = await onAddUser(userToAdd);
      
      // Then send invite if email is provided
      if (newUser.email && createdUser?.id) {
        setIsSendingInvite(true);
        try {
          await inviteUserToLogin(createdUser.id, user.id, newUser.email, newUser.fullName);
          setSuccessMessage(`User ${newUser.fullName} created and invite sent successfully!`);
        } catch (inviteError) {
          console.error('Failed to send invite:', inviteError);
          setSuccessMessage(`User ${newUser.fullName} created but invite failed to send.`);
        } finally {
          setIsSendingInvite(false);
        }
      } else if (newUser.email) {
        setSuccessMessage(`User ${newUser.fullName} created but could not send invite (missing user ID).`);
      } else {
        setSuccessMessage(`User ${newUser.fullName} created successfully!`);
      }
      
      setNewUser({
        fullName: '',
        email: '',
        phoneNumber: '',
        country: '',
        notes: '',
        isAdmin: false,
        sendEmail: false,
      });
      setShowAddUserModal(false);
      
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Failed to add user and send invite:', error);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (currentEditUser) {
      setIsUpdatingUser(true);
      setSuccessMessage(null);
      clearError();
      
      setIsUpdatingUser(true);
      setSuccessMessage(null);
      clearError();
      
      try {
        await onUpdateUser(currentEditUser);
        setSuccessMessage(`User ${currentEditUser.fullName} updated successfully!`);
        setTimeout(() => setSuccessMessage(null), 5000);
        setShowEditForm(false);
        setCurrentEditUser(null);
      } catch (error) {
        console.error('Failed to update user in component:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to update user';
        await utils.handleAsyncOperation(
          () => Promise.reject(new Error(errorMessage)),
          errorMessage
        );
      } finally {
        setIsUpdatingUser(false);
      }
    }
  };

  const handleDeactivateUser = (userToDeactivate: UserType) => {
    setUserToDeactivate(userToDeactivate);
    setShowDeactivateConfirmation(true);
  };

  const confirmDeactivateUser = async () => {
    if (userToDeactivate) {
      try {
        await onUpdateUser({
          ...userToDeactivate,
          deactivated: true,
        });
        setSuccessMessage(`User ${userToDeactivate.fullName} has been deactivated successfully!`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (error) {
        console.error('Failed to deactivate user:', error);
      } finally {
        setShowDeactivateConfirmation(false);
        setUserToDeactivate(null);
      }
    }
  };

  const handleReactivateUser = async (userToReactivate: UserType) => {
    try {
      await onUpdateUser({
        ...userToReactivate,
        deactivated: false,
      });
      setSuccessMessage(`User ${userToReactivate.fullName} has been reactivated successfully!`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Failed to reactivate user:', error);
    }
  };

  const handleDeleteUser = (userToDelete: UserType) => {
    setUserToDelete(userToDelete);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteUser = () => {
    setShowDeleteConfirmation(false);
    setShowDeleteFinalConfirmation(true);
  };

  const confirmDeleteUserFinal = async () => {
    if (userToDelete) {
      try {
        await onRemoveUser(userToDelete.id);
        setSuccessMessage(`User ${userToDelete.fullName} has been permanently deleted!`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (error) {
        console.error('Failed to delete user:', error);
      } finally {
        setShowDeleteFinalConfirmation(false);
        setUserToDelete(null);
      }
    }
  };

  const cancelDeactivateUser = () => {
    setShowDeactivateConfirmation(false);
    setUserToDeactivate(null);
  };

  const cancelDeleteUser = () => {
    setShowDeleteConfirmation(false);
    setUserToDelete(null);
  };

  const cancelDeleteUserFinal = () => {
    setShowDeleteFinalConfirmation(false);
    setUserToDelete(null);
  };

  const handleRemoveUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to remove this user?')) {
      try {
        await onRemoveUser(userId);
      } catch (error) {
        console.error('Failed to remove user in component:', error);
      }
    }
  };

  const handleSuspendUser = async (userToUpdate: UserType) => {
    try {
      await onUpdateUser({
        ...userToUpdate,
        suspended: !userToUpdate.suspended,
      });
    } catch (error) {
      console.error('Failed to suspend/unsuspend user:', error);
    }
  };

  // Only Super Admins can change admin status
  const canManageAdmins = user.isSuperAdmin;
  
  // Check if user can delete users (Super Admin only)
  const canDeleteUsers = user.isSuperAdmin;

  const handleProfilePictureUpload = (userId: string) => {
    setProfilePictureUserId(userId);
    setShowProfilePictureUpload(true);
    clearError();
  };

  const handleProfilePictureProcessed = async (processedImageUrl: string) => {
    if (profilePictureUserId) {
      // The processedImageUrl is already processed by the ProfilePictureUpload component
      // We just need to trigger a refresh of the users list
      setShowProfilePictureUpload(false);
      setProfilePictureUserId(null);
      // In a real app, this would trigger a refresh from the parent component
      window.location.reload();
    }
  };

  const handleProfilePictureCancel = () => {
    setShowProfilePictureUpload(false);
    setProfilePictureUserId(null);
    clearError();
  };

  // Reset Password functionality
  const generateRandomPassword = (): string => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%^&*';
    const allChars = lowercase + uppercase + numbers + special;
    
    let password = '';
    // Ensure at least one character from each category
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Fill remaining 8 characters randomly
    for (let i = 4; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleResetPassword = (userToReset: UserType) => {
    setResetPasswordUser(userToReset);
    setResetPasswordStep('options');
    setShowResetPasswordModal(true);
    setNewPassword('');
    setGeneratedPassword('');
    setEmailNewPassword(false);
  };

  const handleSendResetLink = () => {
    if (resetPasswordUser) {
      // Simulate sending reset link
              // Password reset link sent
      setShowResetPasswordModal(false);
    }
  };

  const handleAutoGeneratePassword = () => {
    const password = generateRandomPassword();
    setGeneratedPassword(password);
    
    if (resetPasswordUser) {
      const updatedUser = {
        ...resetPasswordUser,
        password: password
      };
      onUpdateUser(updatedUser);
      setResetPasswordStep('success');
    }
  };

  const handleCreateNewPassword = () => {
    setResetPasswordStep('create-password');
  };

  const handleSubmitNewPassword = () => {
    if (!newPassword.trim()) {
              // Please enter a new password
      return;
    }
    
    if (resetPasswordUser) {
      const updatedUser = {
        ...resetPasswordUser,
        password: newPassword
      };
      onUpdateUser(updatedUser);
      setResetPasswordStep('success');
    }
  };

  const handleEmailNewPassword = () => {
    if (resetPasswordUser) {
      const passwordToEmail = generatedPassword || newPassword;
              // New password sent
      setShowResetPasswordModal(false);
    }
  };

  const closeResetPasswordModal = () => {
    setShowResetPasswordModal(false);
    setResetPasswordStep('options');
    setResetPasswordUser(null);
    setNewPassword('');
    setGeneratedPassword('');
    setEmailNewPassword(false);
  };

  const handleSendInviteOrReset = async (userToInvite: UserType) => {
    // Clear any previous success message and set loading state
    setSuccessMessage(null);
    clearError();
    setIsSendingInvite(true);
    
    try {
      if (!userToInvite.email) {
        // User does not have an email address - show error
        await utils.handleAsyncOperation(
          () => Promise.reject(new Error('User does not have an email address')),
          'Cannot send invite: User does not have an email address'
        );
        return;
      }

      console.log('🔄 Sending invite/reset for user:', userToInvite.fullName, userToInvite.email);

      if (userToInvite.hasCreatedLogin) {
        // Send password reset link
        console.log('🔄 Sending password reset link...');
        const result = await utils.handleAsyncOperation(
          () => sendPasswordResetLink(
            userToInvite.id,
            user.id,
            userToInvite.email!,
            userToInvite.fullName
          ),
          'Failed to send password reset link'
        );
        
        if (result !== null) {
          console.log('✅ Password reset link sent successfully');
          setSuccessMessage(`Password reset link sent to ${userToInvite.fullName} (${userToInvite.email})`);
          // Auto-clear success message after 5 seconds
          setTimeout(() => setSuccessMessage(null), 5000);
        } else {
          console.log('❌ Password reset link failed - result was null');
        }
      } else {
        // Send login invitation
        console.log('🔄 Sending login invitation...');
        const result = await utils.handleAsyncOperation(
          () => inviteUserToLogin(
            userToInvite.id,
            user.id,
            userToInvite.email!,
            userToInvite.fullName
          ),
          'Failed to send login invitation'
        );
        
        if (result !== null) {
          console.log('✅ Login invitation sent successfully');
          setSuccessMessage(`Login invitation sent to ${userToInvite.fullName} (${userToInvite.email})`);
          // Auto-clear success message after 5 seconds
          setTimeout(() => setSuccessMessage(null), 5000);
        } else {
          console.log('❌ Login invitation failed - result was null');
        }
      }
    } catch (error) {
      console.error('❌ Unexpected error in handleSendInviteOrReset:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unexpected error occurred';
      await utils.handleAsyncOperation(
        () => Promise.reject(new Error(errorMessage)),
        errorMessage
      );
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleSendInviteClick = (userToInvite: UserType) => {
    setUserToInvite(userToInvite);
    setShowSendInviteConfirmation(true);
  };

  const confirmSendInvite = async () => {
    if (userToInvite) {
      setShowSendInviteConfirmation(false);
      await handleSendInviteOrReset(userToInvite);
      setUserToInvite(null);
    }
  };

  const cancelSendInvite = () => {
    setShowSendInviteConfirmation(false);
    setUserToInvite(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">User Management</h3>
              <p className="text-sm text-gray-600">Manage user accounts and permissions</p>
            </div>
            <button
              onClick={() => setShowAddUserModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" /> Add User
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-md">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                <p className="text-red-700 text-sm">{error}</p>
                <button
                  onClick={clearError}
                  className="ml-auto text-red-600 hover:text-red-800"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Success Display */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-md">
              <div className="flex items-center">
                <Check className="h-5 w-5 text-green-600 mr-2" />
                <p className="text-green-700 text-sm">{successMessage}</p>
                <button
                  onClick={() => setSuccessMessage(null)}
                  className="ml-auto text-green-600 hover:text-green-800"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="relative flex-grow">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-3 py-2 border-gray-300 rounded-md"
                  />
                </div>
                <div className="ml-4">
                  <button
                    onClick={() => setShowDeactivatedUsers(!showDeactivatedUsers)}
                    className={`inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                      showDeactivatedUsers
                        ? 'bg-gray-100 text-gray-700 border-gray-400'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                  >
                    <UserX className="h-4 w-4 mr-2" />
                    {showDeactivatedUsers ? 'Hide Deactivated' : 'Show Deactivated'}
                  </button>
                </div>
              </div>
            </div>
            <ul className="divide-y divide-gray-200">
              {filteredUsers.map((userItem) => (
                <li key={userItem.id} className="px-4 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <ProfilePicture 
                          userId={userItem.id}
                          fullName={userItem.fullName}
                          profilePictureUrl={userItem.profilePictureUrl}
                          profilePicture={userItem.profilePicture}
                          size="md"
                        />
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <h2 className="text-lg font-medium text-gray-900">{getDisplayName(userItem)}</h2>
                          {userItem.isSuperAdmin && (
                            <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                              Super Admin
                            </span>
                          )}
                          {userItem.isAdmin && !userItem.isSuperAdmin && (
                            <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              Admin
                            </span>
                          )}
                          {userItem.suspended && (
                            <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Suspended
                            </span>
                          )}
                          {userItem.deactivated && (
                            <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                              Deactivated
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {userItem.email && (
                            <p className="flex items-center mt-1">
                              <Mail className="h-4 w-4 mr-1" />
                              {userItem.email}
                            </p>
                          )}
                          {userItem.phoneNumber && (
                            <p className="flex items-center mt-1">
                              <Phone className="h-4 w-4 mr-1" />
                              {userItem.phoneNumber}
                            </p>
                          )}
                          {userItem.country && (
                            <p className="flex items-center mt-1">
                              🌍 {userItem.country}
                            </p>
                          )}
                          {/* Last Logged In */}
                          <p className="flex items-center mt-1">
                            <Clock className="h-4 w-4 mr-1" />
                            {formatFirestoreTimestamp(userItem.lastLogin)}
                          </p>
                          {/* Created Login Status */}
                          <p className="flex items-center mt-1">
                            {userItem.hasCreatedLogin ? (
                              <>
                                <UserCheck className="h-4 w-4 mr-1 text-green-600" />
                                <span className="text-green-600">Has Created a Login</span>
                              </>
                            ) : (
                              <>
                                <UserX className="h-4 w-4 mr-1 text-red-600" />
                                <span className="text-red-600">Has not Created a Login</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setCurrentEditUser(userItem);
                          setShowEditForm(true);
                        }}
                        className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        title="Edit User"
                      >
                        <Edit className="h-4 w-4" />
                      </button>

                      {/* Send Invite Button - Show for users with email who haven't created login */}
                      {userItem.email && !userItem.hasCreatedLogin && (
                        <button
                          onClick={() => handleSendInviteClick(userItem)}
                          disabled={isSendingInvite || isProcessing}
                          className={`inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white ${
                            isSendingInvite || isProcessing 
                              ? 'bg-gray-400 cursor-not-allowed' 
                              : 'bg-green-600 hover:bg-green-700'
                          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
                          title="Send Login Invitation"
                        >
                          {isSendingInvite ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4" />
                          )}
                        </button>
                      )}

                      {/* Password Reset Button - Show for users who already have login */}
                      {userItem.email && userItem.hasCreatedLogin && (
                        <button
                          onClick={() => handleSendInviteClick(userItem)}
                          disabled={isSendingInvite || isProcessing}
                          className={`inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white ${
                            isSendingInvite || isProcessing 
                              ? 'bg-gray-400 cursor-not-allowed' 
                              : 'bg-orange-600 hover:bg-orange-700'
                          } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500`}
                          title="Send Password Reset Link"
                        >
                          {isSendingInvite ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Key className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleSuspendUser(userItem)}
                        className={`inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white ${
                          userItem.suspended ? 'bg-green-600 hover:bg-green-700' : 'bg-yellow-600 hover:bg-yellow-700'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500`}
                        title={userItem.suspended ? 'Unsuspend User' : 'Suspend User'}
                      >
                        {userItem.suspended ? (
                          <Check className="h-4 w-4" />
                        ) : (
                          <Ban className="h-4 w-4" />
                        )}
                      </button>
                      
                      {/* Deactivate/Reactivate Button */}
                      {canDeleteUsers && (
                        <>
                          {!userItem.deactivated ? (
                            <button
                              onClick={() => handleDeactivateUser(userItem)}
                              className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                              title="Deactivate User"
                            >
                              <UserMinus className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivateUser(userItem)}
                              className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              title="Reactivate User"
                            >
                              <UserCheck className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Delete Button - Only show for deactivated users */}
                          {userItem.deactivated && (
                            <button
                              onClick={() => handleDeleteUser(userItem)}
                              className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              title="Permanently Delete User"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
              {filteredUsers.length === 0 && (
                <li className="px-4 py-6 text-center text-gray-500">
                  {showDeactivatedUsers 
                    ? 'No users found matching your search.' 
                    : 'No active users found matching your search.'
                  }
                </li>
              )}
            </ul>
            
            {/* Show count of hidden deactivated users */}
            {!showDeactivatedUsers && users.some(u => u.deactivated) && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
                <div className="flex items-center justify-between">
                  <span>
                    {users.filter(u => u.deactivated).length} deactivated user{users.filter(u => u.deactivated).length !== 1 ? 's' : ''} hidden
                  </span>
                  <button
                    onClick={() => setShowDeactivatedUsers(true)}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Show them
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add User Modal */}
        {showAddUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
              {/* Compact Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <UserPlus className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Add New User</h3>
                </div>
                <button
                  onClick={() => setShowAddUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Compact Content - No Scrolling */}
              <div className="p-4">
                <form onSubmit={handleAddUserAndSendInvite} className="space-y-4">
                  {/* Basic Information - Compact */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                      <User className="h-4 w-4 mr-1 text-gray-500" />
                      Basic Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={newUser.fullName}
                          onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter full name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={newUser.email}
                          onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="user@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={newUser.phoneNumber}
                          onChange={(e) => setNewUser({ ...newUser, phoneNumber: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Country
                        </label>
                        <select
                          value={newUser.country}
                          onChange={(e) => setNewUser({ ...newUser, country: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select a country</option>
                          {COUNTRIES.map((country: string) => (
                            <option key={country} value={country}>
                              {country}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Notes - Compact */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={newUser.notes}
                      onChange={(e) => setNewUser({ ...newUser, notes: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                      rows={2}
                      placeholder="Add any notes about this user..."
                    />
                  </div>

                  {/* Settings - Compact */}
                  <div className="space-y-2">
                    {/* Only show Admin privileges option to Super Admins */}
                    {user.isSuperAdmin && (
                      <div className="flex items-start space-x-2 p-2 bg-gray-50 rounded border">
                        <input
                          id="isAdmin"
                          name="isAdmin"
                          type="checkbox"
                          checked={newUser.isAdmin}
                          onChange={(e) => setNewUser({ ...newUser, isAdmin: e.target.checked })}
                          className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                        />
                        <div>
                          <label htmlFor="isAdmin" className="text-xs font-medium text-gray-900">
                            Admin privileges
                          </label>
                          <p className="text-xs text-gray-500">Grant administrative access</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-start space-x-2 p-2 bg-blue-50 rounded border">
                      <input
                        id="sendEmail"
                        name="sendEmail"
                        type="checkbox"
                        checked={newUser.sendEmail}
                        onChange={(e) => setNewUser({ ...newUser, sendEmail: e.target.checked })}
                        className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                      />
                      <div>
                        <label htmlFor="sendEmail" className="text-xs font-medium text-gray-900">
                          Send welcome email
                        </label>
                        <p className="text-xs text-gray-500">User receives login instructions</p>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Compact Footer */}
              <div className="flex-shrink-0 border-t border-gray-200 p-4 bg-gray-50">
                <div className="flex flex-col sm:flex-row-reverse gap-2">
                  <button
                    type="submit"
                    onClick={handleAddUserAndSendInvite}
                    className="inline-flex items-center justify-center px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 focus:outline-none focus:ring-1 focus:ring-green-500"
                  >
                    <Send className="w-3 h-3 mr-1" />
                    Add User and Send Invite
                  </button>
                  <button
                    type="button"
                    onClick={handleAddUser}
                    className="inline-flex items-center justify-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <UserPlus className="w-3 h-3 mr-1" />
                    Add User
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddUserModal(false)}
                    className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditForm && currentEditUser && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-10 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
              {/* Compact Header with Profile */}
              <div className="flex justify-between items-center p-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center space-x-3">
                  <ProfilePicture 
                    userId={currentEditUser!.id}
                    fullName={currentEditUser!.fullName}
                    profilePictureUrl={currentEditUser!.profilePictureUrl}
                    profilePicture={currentEditUser!.profilePicture}
                    size="sm"
                  />
                  <div>
                    <h3 className="text-lg font-medium">{currentEditUser!.fullName}</h3>
                    <p className="text-sm text-gray-500">{currentEditUser!.email}</p>
                  </div>
                </div>
                <button
                  className="text-gray-400 hover:text-gray-500"
                  onClick={() => {
                    setShowEditForm(false);
                    setCurrentEditUser(null);
                    setEditUserActiveTab('profile');
                  }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Compact Tab Navigation */}
              <div className="border-b border-gray-200 flex-shrink-0">
                <nav className="-mb-px flex space-x-6 px-4 overflow-x-auto scrollbar-hide">
                  <button
                    onClick={() => setEditUserActiveTab('profile')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 transition-all duration-200 ${
                      editUserActiveTab === 'profile'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <User className="w-4 h-4 inline mr-1" />
                    Profile
                  </button>
                  <button
                    onClick={() => setEditUserActiveTab('permissions')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 transition-all duration-200 ${
                      editUserActiveTab === 'permissions'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Shield className="w-4 h-4 inline mr-1" />
                    Permissions
                  </button>
                  <button
                    onClick={() => setEditUserActiveTab('security')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 transition-all duration-200 ${
                      editUserActiveTab === 'security'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Key className="w-4 h-4 inline mr-1" />
                    Security
                  </button>
                  <button
                    onClick={() => setEditUserActiveTab('notes')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-w-fit flex-shrink-0 transition-all duration-200 ${
                      editUserActiveTab === 'notes'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Info className="w-4 h-4 inline mr-1" />
                    Notes
                  </button>
                </nav>
              </div>

              {/* Form wrapper */}
              <form onSubmit={handleEditUser} className="flex-1 flex flex-col">
                {/* Compact Scrollable Content Area */}
                <div className="flex-1 overflow-y-auto">
                  <div className="p-4">
                                      {editUserActiveTab === 'profile' && (
                      <div className="space-y-4">
                        <h4 className="text-base font-medium text-gray-900">Profile Information</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Full Name</label>
                          <input
                            type="text"
                            value={currentEditUser!.fullName || ''}
                            onChange={(e) => setCurrentEditUser({ ...currentEditUser!, fullName: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Email</label>
                          <input
                            type="email"
                            value={currentEditUser!.email || ''}
                            onChange={(e) => setCurrentEditUser({ ...currentEditUser!, email: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                          <input
                            type="tel"
                            value={currentEditUser!.phoneNumber || ''}
                            onChange={(e) => setCurrentEditUser({ ...currentEditUser!, phoneNumber: e.target.value })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Country</label>
                          {user.isSuperAdmin ? (
                            <select
                              value={currentEditUser!.country || ''}
                              onChange={(e) => setCurrentEditUser({ ...currentEditUser!, country: e.target.value })}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            >
                              <option value="">Select a country</option>
                              {COUNTRIES.map((country: string) => (
                                <option key={country} value={country}>
                                  {country}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={currentEditUser!.country || 'Not provided'}
                              disabled
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 text-gray-500 sm:text-sm"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {editUserActiveTab === 'permissions' && (
                    <div className="space-y-4">
                      <h4 className="text-base font-medium text-gray-900">Permissions</h4>
                      
                      {/* Admin Status - Only Super Admins can change this */}
                      {canManageAdmins && (
                        <div>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={currentEditUser!.isAdmin || false}
                              onChange={(e) => setCurrentEditUser({ ...currentEditUser!, isAdmin: e.target.checked })}
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-700">Admin privileges</span>
                          </label>
                          <p className="mt-1 text-xs text-gray-500">Grant this user administrative access</p>
                        </div>
                      )}
                      
                      {/* Suspension Status */}
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                                                          checked={currentEditUser!.suspended || false}
                              onChange={(e) => setCurrentEditUser({ ...currentEditUser!, suspended: e.target.checked })}
                            className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Suspend user account</span>
                        </label>
                        <p className="mt-1 text-xs text-gray-500">Suspended users cannot access the system</p>
                      </div>
                      
                      {/* Deactivation Status */}
                      <div>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                                                          checked={currentEditUser!.deactivated || false}
                              onChange={(e) => setCurrentEditUser({ ...currentEditUser!, deactivated: e.target.checked })}
                            className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Deactivate user account</span>
                        </label>
                        <p className="mt-1 text-xs text-gray-500">Deactivated users cannot access the system and their data is preserved</p>
                      </div>
                    </div>
                  )}



                  {editUserActiveTab === 'security' && (
                    <div className="space-y-4">
                      <h4 className="text-base font-medium text-gray-900">Security & Login</h4>
                      
                      {/* Compact Info Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-gray-50 p-3 rounded-md">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Last Logged In</label>
                          <div className="text-sm text-gray-800">
                            {currentEditUser!.lastLogin ? formatFirestoreTimestamp(currentEditUser!.lastLogin) : 'Never'}
                          </div>
                        </div>
                        
                        <div className="bg-gray-50 p-3 rounded-md">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Login Status</label>
                          <div className="flex items-center">
                            {currentEditUser!.hasCreatedLogin ? (
                              <>
                                <UserCheck className="h-4 w-4 mr-1 text-green-600" />
                                <span className="text-sm text-green-600 font-medium">Active</span>
                              </>
                            ) : (
                              <>
                                <UserX className="h-4 w-4 mr-1 text-red-600" />
                                <span className="text-sm text-red-600 font-medium">Inactive</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Compact Actions */}
                      {currentEditUser.email && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-2">Quick Actions</label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleSendInviteClick(currentEditUser!)}
                              disabled={isSendingInvite || isProcessing}
                              className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border ${
                                isSendingInvite || isProcessing
                                  ? 'border-gray-300 text-gray-500 bg-gray-100 cursor-not-allowed'
                                                                      : currentEditUser!.hasCreatedLogin 
                                    ? 'border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100' 
                                    : 'border-green-300 text-green-700 bg-green-50 hover:bg-green-100'
                              }`}
                            >
                              {isSendingInvite ? (
                                <>
                                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Mail className="h-3 w-3 mr-1" />
                                  {currentEditUser.hasCreatedLogin ? 'Reset Link' : 'Send Invite'}
                                </>
                              )}
                            </button>
                            
                            <button
                              type="button"
                              onClick={() => handleResetPassword(currentEditUser!)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded border border-red-300 text-red-700 bg-red-50 hover:bg-red-100"
                            >
                              <Key className="h-3 w-3 mr-1" />
                              Password
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {editUserActiveTab === 'notes' && (
                    <div className="space-y-4">
                      <h4 className="text-base font-medium text-gray-900">Notes</h4>
                      <textarea
                        value={currentEditUser!.notes || ''}
                        onChange={(e) => setCurrentEditUser({ ...currentEditUser!, notes: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        rows={4}
                        placeholder="Add any notes for this user..."
                      />
                    </div>
                  )}

                  </div>
                </div>
                
                {/* Compact Action Buttons */}
                <div className="flex-shrink-0 border-t border-gray-200 p-4">
                  <div className="flex flex-row-reverse gap-3">
                    <button
                      type="submit"
                      disabled={isUpdatingUser || isProcessing}
                      className={`px-4 py-2 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 inline-flex items-center ${
                        isUpdatingUser || isProcessing
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {isUpdatingUser ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={() => {
                        setShowEditForm(false);
                        setCurrentEditUser(null);
                        setEditUserActiveTab('profile');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {showResetPasswordModal && resetPasswordUser && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-20">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Reset Password</h3>
                <button
                  className="text-gray-400 hover:text-gray-500"
                  onClick={closeResetPasswordModal}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {resetPasswordStep === 'options' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Choose how to reset the password for <strong>{resetPasswordUser.fullName}</strong>:
                  </p>
                  
                  <div className="space-y-3">
                    <button
                      onClick={handleSendResetLink}
                      className="w-full flex items-center px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Mail className="h-5 w-5 mr-3 text-blue-600" />
                      <div className="text-left">
                        <div className="font-medium">Send reset link</div>
                        <div className="text-sm text-gray-500">User receives email with reset link</div>
                      </div>
                    </button>

                    <button
                      onClick={handleAutoGeneratePassword}
                      className="w-full flex items-center px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Key className="h-5 w-5 mr-3 text-green-600" />
                      <div className="text-left">
                        <div className="font-medium">Auto-generate new password</div>
                        <div className="text-sm text-gray-500">Create secure 12-character password</div>
                      </div>
                    </button>

                    <button
                      onClick={handleCreateNewPassword}
                      className="w-full flex items-center px-4 py-3 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Edit className="h-5 w-5 mr-3 text-purple-600" />
                      <div className="text-left">
                        <div className="font-medium">Create new password</div>
                        <div className="text-sm text-gray-500">Manually enter a new password</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {resetPasswordStep === 'create-password' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Enter a new password for <strong>{resetPasswordUser.fullName}</strong>:
                  </p>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter new password"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => setResetPasswordStep('options')}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSubmitNewPassword}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Set Password
                    </button>
                  </div>
                </div>
              )}

              {resetPasswordStep === 'success' && (
                <div>
                  <div className="text-center mb-4">
                    <Check className="h-12 w-12 text-green-500 mx-auto mb-2" />
                    <p className="text-lg font-medium text-gray-900">Password Updated</p>
                    <p className="text-sm text-gray-600">
                      The password for <strong>{resetPasswordUser.fullName}</strong> has been successfully updated.
                    </p>
                  </div>

                  {(generatedPassword || newPassword) && (
                    <div className="space-y-4 mb-4">
                      <div className="bg-gray-50 border rounded-md p-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Password:</label>
                        <div className="flex items-center space-x-2">
                          <code className="flex-1 text-sm bg-white border rounded px-2 py-1 font-mono">
                            {generatedPassword || newPassword}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(generatedPassword || newPassword);
                              // Password copied to clipboard
                            }}
                            className="p-1 text-gray-500 hover:text-gray-700"
                            title="Copy to clipboard"
                          >
                            <ClipboardCopy className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {resetPasswordUser.email && (
                        <button
                          onClick={() => setShowEmailConfirmation(true)}
                          className="w-full flex items-center justify-center px-4 py-2 border border-blue-300 rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Email password to user
                        </button>
                      )}
                    </div>
                  )}

                  <button
                    onClick={closeResetPasswordModal}
                    className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add email confirmation modal */}
        {showEmailConfirmation && resetPasswordUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-medium mb-4">Confirm Email Password</h3>
              <p className="text-sm text-gray-600 mb-4">
                The new password will be sent to <strong>{resetPasswordUser.email}</strong>. Would you like to proceed?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowEmailConfirmation(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await sendEmailToUser(resetPasswordUser, {
                        subject: 'Your Password Has Been Reset',
                        body: `
                          <h2>Password Reset Notification</h2>
                          <p>Your password has been reset by an administrator.</p>
                          <p>Your new password is: <strong>${generatedPassword || newPassword}</strong></p>
                          <p>Please log in with this password and change it immediately for security purposes.</p>
                        `,
                        template: 'password-reset'
                      }, user.id);
                      setShowEmailConfirmation(false);
                      closeResetPasswordModal();
                    } catch (error) {
                      console.error('Failed to send password email:', error);
                      // Failed to send email
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Confirm and Send
                </button>
              </div>
            </div>
          </div>
        )}

                 {/* Send Invite/Reset Confirmation Modal */}
         {showSendInviteConfirmation && userToInvite && (
           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
             <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
               <h3 className="text-lg font-medium mb-4">
                 {userToInvite.hasCreatedLogin ? 'Confirm Password Reset' : 'Confirm Send Invite'}
               </h3>
               <p className="text-sm text-gray-600 mb-2">
                 {userToInvite.hasCreatedLogin 
                   ? `Are you sure you want to send a password reset link to` 
                   : `Are you sure you want to send a login invitation to`
                 } <strong>{userToInvite.fullName}</strong>?
               </p>
               {userToInvite.email && (
                 <p className="text-sm text-gray-500 mb-4">
                   Email will be sent to: <strong>{userToInvite.email}</strong>
                 </p>
               )}
               <div className="flex space-x-3">
                 <button
                   onClick={cancelSendInvite}
                   className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                 >
                   Cancel
                 </button>
                 <button
                   onClick={confirmSendInvite}
                   disabled={isSendingInvite}
                   className={`flex-1 px-4 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 inline-flex items-center justify-center ${
                     isSendingInvite
                       ? 'bg-gray-400 cursor-not-allowed'
                       : userToInvite.hasCreatedLogin 
                         ? 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500' 
                         : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                   }`}
                 >
                   {isSendingInvite ? (
                     <>
                       <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                       Sending...
                     </>
                   ) : (
                     userToInvite.hasCreatedLogin ? 'Send Reset Link' : 'Send Invite'
                   )}
                 </button>
               </div>
             </div>
           </div>
         )}

        {/* Deactivate User Confirmation Modal */}
        {showDeactivateConfirmation && userToDeactivate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <UserMinus className="h-6 w-6 text-orange-600 mr-3" />
                <h3 className="text-lg font-medium">Confirm Deactivation</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to deactivate <strong>{userToDeactivate.fullName}</strong>?
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Deactivated users will not be able to access the system, but their data will be preserved. You can reactivate them later.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={cancelDeactivateUser}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeactivateUser}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                >
                  Deactivate User
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Confirmation Modal */}
        {showDeleteConfirmation && userToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <Trash2 className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-medium">Confirm Deletion</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to permanently delete <strong>{userToDelete.fullName}</strong>?
              </p>
              <p className="text-sm text-red-600 mb-6">
                <strong>Warning:</strong> This action cannot be undone. All user data will be permanently removed.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={cancelDeleteUser}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteUser}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Final Delete Confirmation Modal */}
        {showDeleteFinalConfirmation && userToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-medium">Final Confirmation</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                This is your final warning. You are about to permanently delete:
              </p>
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-6">
                <p className="text-sm font-medium text-red-800">{userToDelete.fullName}</p>
                <p className="text-xs text-red-600">{userToDelete.email}</p>
              </div>
              <p className="text-sm text-red-600 mb-6">
                <strong>This action is irreversible.</strong> All user data, posts, and activity will be permanently removed from the system.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={cancelDeleteUserFinal}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteUserFinal}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Permanently Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showProfilePictureUpload && profilePictureUserId && (
          <ProfilePictureUpload
            currentImageUrl={users.find(u => u.id === profilePictureUserId)?.profilePictureUrl}
            onImageProcessed={handleProfilePictureProcessed}
            onCancel={handleProfilePictureCancel}
            userName={users.find(u => u.id === profilePictureUserId)?.fullName || 'User'}
            isUploading={isProcessing}
          />
        )}
      </main>
    </div>
  );
};

export default UsersAdmin; 